import { observer } from "mobx-react-lite"
import Layout from "../../components/layout/Layout"
import { useStore } from "../../stores/root-store-context";
import { Button, Flex, Form, FormProps, Input, Modal, Popconfirm, Select, Image, Spin, Table, TableProps, Tag, Typography, message, DatePicker, App, Checkbox, Switch } from "antd";
import { useEffect, useState } from "react";
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Link, useNavigate } from "react-router-dom";
import { NewProject, Project } from "../../stores/ProjectsStore";
import TextArea from "antd/es/input/TextArea";
import { DateSort, TextSort } from "../../helpers/SorterHelper";
import { useTranslation } from "react-i18next";
import { getDateLocale } from '../../helpers/dateLocalization';
import { slugify } from "../../routes/RouterUtilities";
import FavoriteStar from "../../components/projects/FavoriteStar";
import { Roles } from "../../enums/Roles";
import { TariffPlan } from "../../enums/TariffPlan";

const Search = Input.Search;

const ProjectsList = observer(() => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { applicationStore, projectsStore, favoriteProjectsStore } = useStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredProject, setFilteredProject] = useState<Project[]>([]);
  const [expandedSkills, setExpandedSkills] = useState<{ [key: string]: boolean }>({});
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  const dateLocale = getDateLocale(i18n.language);

  const showModal = () => setIsModalOpen(true);
  const hideModal = () => setIsModalOpen(false);

  const getNavigateToProject = (): string => {
    if ([TariffPlan.No_Tariff, TariffPlan.Basic].includes(applicationStore.company.TariffPlanId as TariffPlan)) {
      return ``;
    } else {
      return `/tasks`;
    }
  };

  const columns: TableProps<Project>['columns'] = [
    {
      title: '',
      dataIndex: 'favorite',
      key: 'favorite',
      width: 50,
      render: (_: any, record: Project) => <FavoriteStar projectId={Number(record.id)} />
    },
    {
      title: `${t("Title")}`,
      dataIndex: 'title',
      key: 'title',
      render: (_: any, record: Project) =>
        <Link to={`./${slugify(record.title || record.name || '')}${getNavigateToProject()}`}>{record.title}</Link>,
      sorter: (a: Project, b: Project) => TextSort(a.title, b.title)
    },
    {
      title: `${t("StartDate")}`,
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => <div>{date.format('YYYY-MM-DD')}</div>,
      sorter: (a: Project, b: Project) =>
        DateSort(a.startDate?.format('YYYY-MM-DD'), b.startDate?.format('YYYY-MM-DD'))
    },
    {
      title: `${t("ProjectManager")}`,
      dataIndex: 'projectResponsible',
      key: 'projectResponsible',
      render: (user) => <div>{user?.email}</div>,
      sorter: (a: Project, b: Project) => TextSort(a.projectResponsible?.email, b.projectResponsible?.email)
    },
    {
      title: `${t("RequiredSkills")}`,
      dataIndex: 'projectSkills',
      key: 'projectSkills',
      render: (skills, project) =>
        <Flex wrap style={{ maxWidth: 200 }} gap={10}>
          {(expandedSkills[project.id] ? skills : skills.slice(0, 5)).map((el: any) => (
            <Tag color="blue" key={el.skill.title}>{el.skill.title}</Tag>
          ))}
          {skills?.length > 5 && (
            expandedSkills[project.id] ? (
              <Tag
                onClick={() => setExpandedSkills(prev => ({ ...prev, [project.id]: false }))}
                style={{ cursor: 'pointer' }}
                color="blue"
              >
                Show less
              </Tag>
            ) : (
              <Tag
                onClick={() => setExpandedSkills(prev => ({ ...prev, [project.id]: true }))}
                style={{ cursor: 'pointer' }}
                color="blue"
              >
                +{skills.length - 5} more
              </Tag>
            )
          )}
        </Flex>,
      filters: applicationStore.skills?.map(skill => ({ text: skill.title, value: skill.skillId })),
      onFilter: (value, record) => record.projectSkills.some((el: any) => el.skillId === value),
    },
    {
      title: '',
      dataIndex: 'operation',
      render: (_: any, record: Project) => (
        <Flex justify="space-between">
          <Button
            type="primary"
            shape="circle"
            onClick={() => navigate(`./${slugify(record.title || record.name || '')}${getNavigateToProject()}`)}
            icon={<EyeOutlined />}
          />
          <Popconfirm
            title="Delete the project"
            description="Are you sure to delete this project?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => {
              projectsStore.deleteById(record?.id)
                .then(() => message.success("The project was deleted!"))
                .catch(error => message.error(error.message))
                .finally(update);
            }}
          >
            <Button
              disabled={!(applicationStore.user?.roles?.some(r =>
                r.id === Roles.Owner || r.id === Roles.Admin || r.id === Roles.Manager || r.id === Roles.Financier))}
              type="primary"
              danger
              shape="circle"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Flex>
      ),
    }
  ];

  const getFilteredProjects = () => {
    let data = [...projectsStore.projects];

    data.sort((a, b) => {
      const aFav = favoriteProjectsStore.ids.has(Number(a.id));
      const bFav = favoriteProjectsStore.ids.has(Number(b.id));
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return 0;
    });

    if (searchText.length > 0) {
      data = data.filter(el => el.title?.toLowerCase().includes(searchText.toLowerCase()));
    }

    data = data.map(el => ({ ...el, key: el.id }));
    return data;
  };

  const update = async () => {
    await projectsStore.getAll(true); // force refetch for the current tenant
    if (applicationStore.user?.id) {
      await favoriteProjectsStore.getMyFavorites(Number(applicationStore.user.id));
    }
    setFilteredProject(getFilteredProjects());
  };

  useEffect(() => {
    update();
    // re-run when company (tenant) changes
  }, [applicationStore.company?.Tenant]);

  useEffect(() => {
    setFilteredProject(getFilteredProjects());
  }, [searchText, favoriteProjectsStore.ids.size, projectsStore.projects.length]);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  return (
    <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={`${t("Projects")}`}>
      <div className="container" style={{ margin: "20px auto" }}>
        <Spin spinning={projectsStore.isLoading}>
          {applicationStore.isFreezeModalVisible ? (
            <p style={{ padding: '20px', textAlign: 'center' }}>
              Access to projects is limited until the subscription is renewed.
            </p>
          ) : (
            <>
              <Flex justify="space-between">
                <Search
                  style={{ width: 300 }}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Button
                  disabled={!(applicationStore.user?.roles?.some(r =>
                    r.id === Roles.Owner || r.id === Roles.Manager || r.id === Roles.Financier || r.id === Roles.Admin))}
                  type="primary"
                  style={{ float: "right", marginBottom: 20, borderRadius: 0 }}
                  onClick={showModal}
                >
                  {`${t("Add")}`}
                </Button>
              </Flex>
              <Table
                columns={columns}
                dataSource={filteredProject}
                rowHoverable
                pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '50', '100'] }}
              />
            </>
          )}
        </Spin>
      </div>

      <Modal
        key={currentLanguage}
        title={`${t("Create")} ${t("Project")}`}
        open={isModalOpen}
        footer={null}
        onCancel={hideModal}
      >
        <Form
          key={currentLanguage}
          form={form}
          onFinish={onFinish}
          layout="vertical"
        >
          <Form.Item<Project>
            label={`${t("Title")}`}
            name="title"
            style={{ width: "100%" }}
            rules={[{ required: true }]}
          >
            <Input placeholder={`${t("Title")}`} />
          </Form.Item>
          <Form.Item<Project>
            label={`${t("Description")}`}
            name="description"
            style={{ width: "100%" }}
            rules={[{ required: true }]}
          >
            <TextArea rows={4} placeholder={`${t("Description")}`} />
          </Form.Item>
          <Form.Item<Project>
            label={`${t("StartDate")}`}
            name="startDate"
            style={{ width: "100%" }}
            rules={[{ required: true }]}
          >
            <DatePicker
              key={currentLanguage}
              locale={dateLocale}
              placeholder={`${t("StartDate")}`}
            />
          </Form.Item>
          <Form.Item<Project>
            label={`${t("IsActive")} ${t("Project")}`}
            name="isActive"
            style={{ width: "100%" }}
            initialValue={true}
            hidden
          >
            <Switch />
          </Form.Item>
          <Form.Item<Project>
            label={`${t("IsPublish")} ${t("Project")}`}
            name="isPublish"
            style={{ width: "100%" }}
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" className="theme-btn" htmlType="submit" style={{ height: 65, float: "right" }}>
              {`${t("Create")}`} <i className="fa-solid fa-arrow-right-long" />
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );

  async function onFinish(project: NewProject) {
    message.open({ type: 'loading', content: 'Creating project...', key: 'project' });

    projectsStore.createNew(project)
      .then(() => {
        message.destroy("project");
        message.success("The project was created");
        projectsStore.getAll(true).then(() => setFilteredProject(projectsStore.projects));
        form.resetFields();
        hideModal();
      })
      .catch(error => {
        message.destroy("project");
        message.error(error.message);
      });
  }
});
export default ProjectsList;
